import {
    Box,
    Button,
    ButtonGroup,
    Card,
    CardBody,
    CardHeader,
    FormControl,
    FormErrorMessage,
    FormLabel,
    Input,
    Select,
    Text,
} from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import { translate } from '../../../i18n';
import { CreateTimeWindowDiscountDto, UserMembershipDiscountType } from '../../../models/user-membership/dto/user-membership.dto';
import {
    MEMBERSHIP_DISCOUNT_ERROR_KEYS,
    validateMembershipDiscount,
} from '../utils/membership-discount';
import { useGetTimeWindowGroups } from '../../time-windows/hooks/useGetTimeWindowGroups';

type BookingParametersMode = 'default' | string; // string represents timeWindowGroupId

interface BookingParametersSegmentedControlProps {
    organizationId?: string;
    maxPrivateCourtBookings: number;
    maxPrivateCourtBookingsDuration: number;
    maxCourtBookingTimeInAdvance: number;
    courtBookingDiscountType: UserMembershipDiscountType;
    courtBookingDiscountValue: number;
    timeWindowDiscounts: CreateTimeWindowDiscountDto[];
    onMaxPrivateCourtBookingsChange: (value: number) => void;
    onMaxPrivateCourtBookingsDurationChange: (value: number) => void;
    onMaxCourtBookingTimeInAdvanceChange: (value: number) => void;
    onCourtBookingDiscountTypeChange: (value: UserMembershipDiscountType) => void;
    onCourtBookingDiscountValueChange: (value: number) => void;
    onTimeWindowDiscountsChange: (discounts: CreateTimeWindowDiscountDto[]) => void;
}

export default function BookingParametersSegmentedControl({
    organizationId,
    maxPrivateCourtBookings,
    maxPrivateCourtBookingsDuration,
    maxCourtBookingTimeInAdvance,
    courtBookingDiscountType,
    courtBookingDiscountValue,
    timeWindowDiscounts,
    onMaxPrivateCourtBookingsChange,
    onMaxPrivateCourtBookingsDurationChange,
    onMaxCourtBookingTimeInAdvanceChange,
    onCourtBookingDiscountTypeChange,
    onCourtBookingDiscountValueChange,
    onTimeWindowDiscountsChange,
}: BookingParametersSegmentedControlProps) {
    const [mode, setMode] = useState<BookingParametersMode>('default');
    const { data: timeWindowGroups = [], isLoading, isError } = useGetTimeWindowGroups(organizationId);

    /**
     * R92-4 — the sharpest instance of the class, because the symptom is not an
     * empty state at all: it is a silent change to WHICH SET OF LIMITS the
     * operator is editing.
     *
     * Post-R89-1 a refused `GET time-windows/organizations/:id/groups` leaves
     * `data === undefined`; `= []` then made `find()` miss, and this effect
     * reset the segmented control to `default`. So a time-window group that was
     * selected a moment ago (this query refetches on every window focus —
     * `staleTime` is 30 s) vanished from the tab strip and the operator was
     * moved onto the DEFAULT booking parameters without being told. Whatever
     * they typed next applied to every member at every hour instead of to the
     * off-peak override they had open.
     *
     * The reset itself is correct behaviour for a group that was genuinely
     * DELETED, which is why it is kept — it is only gated on having actually
     * read the list. `isError` is the whole difference between "this group is
     * gone" and "we could not find out".
     */
    useEffect(() => {
        if (isError) return;
        if (mode !== 'default' && !timeWindowGroups.find(g => g.id === mode)) {
            setMode('default');
        }
    }, [timeWindowGroups, mode, isError]);

    // Get the current values based on mode
    const getCurrentValues = () => {
        if (mode === 'default') {
            return {
                maxPrivateCourtBookings,
                maxPrivateCourtBookingsDuration: maxPrivateCourtBookingsDuration / (24 * 60 * 60 * 1000), // Convert from ms to days
                maxCourtBookingTimeInAdvance: maxCourtBookingTimeInAdvance / (24 * 60 * 60 * 1000), // Convert from ms to days
                courtBookingDiscountType,
                courtBookingDiscountValue,
            };
        }

        const timeWindowDiscount = timeWindowDiscounts.find(
            (d) => d.timeWindowGroupId === mode
        );

        if (!timeWindowDiscount) {
            return {
                maxPrivateCourtBookings,
                maxPrivateCourtBookingsDuration: maxPrivateCourtBookingsDuration / (24 * 60 * 60 * 1000),
                maxCourtBookingTimeInAdvance: maxCourtBookingTimeInAdvance / (24 * 60 * 60 * 1000),
                courtBookingDiscountType,
                courtBookingDiscountValue,
            };
        }

        return {
            maxPrivateCourtBookings: timeWindowDiscount.maxPrivateCourtBookings,
            maxPrivateCourtBookingsDuration: timeWindowDiscount.maxPrivateCourtBookingsDuration / (24 * 60 * 60 * 1000),
            maxCourtBookingTimeInAdvance: timeWindowDiscount.maxCourtBookingTimeInAdvance / (24 * 60 * 60 * 1000),
            courtBookingDiscountType: timeWindowDiscount.courtBookingDiscountType,
            courtBookingDiscountValue: timeWindowDiscount.courtBookingDiscountValue,
        };
    };

    const handleValueChange = (
        field: keyof typeof currentValues,
        value: number | UserMembershipDiscountType,
        isDays = false
    ) => {
        const finalValue = isDays ? (value as number) * 24 * 60 * 60 * 1000 : value;

        if (mode === 'default') {
            // Update default values
            switch (field) {
                case 'maxPrivateCourtBookings':
                    onMaxPrivateCourtBookingsChange(finalValue as number);
                    break;
                case 'maxPrivateCourtBookingsDuration':
                    onMaxPrivateCourtBookingsDurationChange(finalValue as number);
                    break;
                case 'maxCourtBookingTimeInAdvance':
                    onMaxCourtBookingTimeInAdvanceChange(finalValue as number);
                    break;
                case 'courtBookingDiscountType':
                    onCourtBookingDiscountTypeChange(value as UserMembershipDiscountType);
                    break;
                case 'courtBookingDiscountValue':
                    onCourtBookingDiscountValueChange(value as number);
                    break;
            }
        } else {
            // Update time window specific values
            const updatedDiscounts = timeWindowDiscounts.map(discount => {
                if (discount.timeWindowGroupId === mode) {
                    return {
                        ...discount,
                        [field]: finalValue,
                    };
                }
                return discount;
            });

            // If this time window doesn't exist yet, create it
            if (!timeWindowDiscounts.find(d => d.timeWindowGroupId === mode)) {
                updatedDiscounts.push({
                    timeWindowGroupId: mode,
                    maxPrivateCourtBookings: field === 'maxPrivateCourtBookings' ? finalValue as number : maxPrivateCourtBookings,
                    maxPrivateCourtBookingsDuration: field === 'maxPrivateCourtBookingsDuration' ? finalValue as number : maxPrivateCourtBookingsDuration,
                    maxCourtBookingTimeInAdvance: field === 'maxCourtBookingTimeInAdvance' ? finalValue as number : maxCourtBookingTimeInAdvance,
                    courtBookingDiscountType: field === 'courtBookingDiscountType' ? value as UserMembershipDiscountType : courtBookingDiscountType,
                    courtBookingDiscountValue: field === 'courtBookingDiscountValue' ? value as number : courtBookingDiscountValue,
                });
            }

            onTimeWindowDiscountsChange(updatedDiscounts);
        }
    };

    const currentValues = getCurrentValues();
    // H16 — reports the pair currently ON SCREEN. The submit gate lives in
    // AddUserMembershipTypeModal and checks every pair, including ones the
    // admin has navigated away from; this only decides what the field says.
    const courtBookingDiscountError = validateMembershipDiscount(
        currentValues.courtBookingDiscountType,
        currentValues.courtBookingDiscountValue,
    );

    if (isLoading) {
        return <Box>{translate('time_windows.loading')}</Box>;
    }

    /**
     * The tab strip is the only way into a per-time-window override, so a
     * refused read cannot be rendered as "this organization has no groups": the
     * card would show a lone "Default values" tab and the operator would edit
     * the org-wide limits believing the overrides do not exist. The default
     * fields are withheld with it — they are the ones that would be edited by
     * mistake.
     */
    if (isError) {
        return (
            <Card variant="outline">
                <CardBody>
                    <Text color="danger.500" fontSize="sm">
                        {translate('time_windows.error_loading')}
                    </Text>
                </CardBody>
            </Card>
        );
    }

    return (
        <Card variant="outline">
            <CardHeader>
                <ButtonGroup isAttached width="100%">
                    <Button
                        flex={1}
                        colorScheme={mode === 'default' ? 'brand' : 'gray'}
                        onClick={() => setMode('default')}
                    >
                        {translate('userMembership.default_values')}
                    </Button>
                    {timeWindowGroups.map((group) => (
                        <Button
                            key={group.id}
                            flex={1}
                            colorScheme={mode === group.id ? 'brand' : 'gray'}
                            onClick={() => setMode(group.id)}
                        >
                            {group.name}
                        </Button>
                    ))}
                </ButtonGroup>
            </CardHeader>
            <CardBody>
                <FormControl>
                    <FormLabel>{translate('userMembership.maxPrivateCourtBookings')}</FormLabel>
                    <Box display="flex" alignItems="center" gap={2}>
                        <Input
                            width="100px"
                            type="number"
                            // H61 — same treatment as the discount field below.
                            // `parseInt('', 10)` is NaN, which reached an
                            // `int not null` column and came back as a 500
                            // (Postgres saw the unquoted NaN as an identifier:
                            // `column "nan" does not exist`). Render cleared as
                            // empty and send NaN, which the server now rejects
                            // with a legible 400.
                            value={Number.isFinite(currentValues.maxPrivateCourtBookings)
                                ? currentValues.maxPrivateCourtBookings
                                : ''}
                            onChange={(e) => handleValueChange('maxPrivateCourtBookings', e.target.value === '' ? NaN : Number(e.target.value))}
                        />
                        <Text>{translate('userMembership.bookings_per')}</Text>
                        <Input
                            width="100px"
                            type="number"
                            // H61 — `isDays` multiplies by 86_400_000, and
                            // `NaN * n` is still NaN, so this one landed on a
                            // `bigint not null` as `invalid input syntax for
                            // type bigint: "NaN"`.
                            value={Number.isFinite(currentValues.maxPrivateCourtBookingsDuration)
                                ? currentValues.maxPrivateCourtBookingsDuration
                                : ''}
                            onChange={(e) => handleValueChange('maxPrivateCourtBookingsDuration', e.target.value === '' ? NaN : Number(e.target.value), true)}
                        />
                        <Text>{translate('userMembership.days')}</Text>
                    </Box>
                </FormControl>

                <FormControl mt={4}>
                    <FormLabel>{translate('userMembership.maxCourtBookingTimeInAdvance')}</FormLabel>
                    <Box display="flex" alignItems="center" gap={2}>
                        <Input
                            type="number"
                            // H61 — see above; same bigint column.
                            value={Number.isFinite(currentValues.maxCourtBookingTimeInAdvance)
                                ? currentValues.maxCourtBookingTimeInAdvance
                                : ''}
                            onChange={(e) => handleValueChange('maxCourtBookingTimeInAdvance', e.target.value === '' ? NaN : Number(e.target.value), true)}
                        />
                        <Text>{translate('userMembership.days')}</Text>
                    </Box>
                </FormControl>

                <FormControl mt={4} isInvalid={Boolean(courtBookingDiscountError)}>
                    <FormLabel>{translate('userMembership.courtBookingDiscountValue')}</FormLabel>
                    <Box display="flex" alignItems="center" justifyContent="center" gap={4}>
                        <Input
                            flex={1}
                            type="number"
                            step="1"
                            min="0"
                            aria-label={translate('userMembership.courtBookingDiscountValue')}
                            // H16 — see the coaching field in AddUserMembershipTypeModal.
                            // This ONE input writes either the default pair or the selected
                            // time window's pair depending on `mode`, so both were exposed
                            // to `parseInt`'s NaN-on-clear (a `null` into `int not null`)
                            // and to its silent truncation of a fractional rate.
                            value={Number.isFinite(currentValues.courtBookingDiscountValue)
                                ? currentValues.courtBookingDiscountValue
                                : ''}
                            onChange={(e) => handleValueChange(
                                'courtBookingDiscountValue',
                                e.target.value === '' ? NaN : Number(e.target.value),
                            )}
                        />
                        <Select
                            flex={1}
                            value={currentValues.courtBookingDiscountType}
                            onChange={(e) => handleValueChange('courtBookingDiscountType', e.target.value as UserMembershipDiscountType)}
                        >
                            <option value={UserMembershipDiscountType.PERCENTAGE}>%</option>
                            <option value={UserMembershipDiscountType.CONSTANT}>{translate('userMembership.courtBookingDiscountType_constant')}</option>
                        </Select>
                    </Box>
                    {courtBookingDiscountError ? (
                        <FormErrorMessage>
                            {translate(MEMBERSHIP_DISCOUNT_ERROR_KEYS[courtBookingDiscountError])}
                        </FormErrorMessage>
                    ) : null}
                </FormControl>
            </CardBody>
        </Card>
    );
}